import { useEffect, useRef } from 'react';
import {
  redoHistory,
  setHistoryRecordingEnabled,
  undoHistory,
  type HistoryResult,
} from './historyStore';

/** A press, reported to the page so it can show the R20 message. */
export type HistoryPressResult = { direction: 'undo' | 'redo' } & HistoryResult;

interface Options {
  /** Called after every claimed press with what it did (R20). */
  onResult?: (result: HistoryPressResult) => void;
  /** R8: only while the report is editable. */
  enabled: boolean;
}

interface Chord {
  key: string;
  ctrl?: boolean;
  meta?: boolean;
  shift?: boolean;
}

/** Spec §5 key mappings. */
const UNDO_CHORDS: Chord[] = [
  { key: 'z', meta: true }, // macOS
  { key: 'z', ctrl: true }, // Windows / Linux
];
const REDO_CHORDS: Chord[] = [
  { key: 'z', meta: true, shift: true }, // macOS
  { key: 'y', ctrl: true }, // Windows / Linux
  { key: 'z', ctrl: true, shift: true }, // Windows / Linux
];

/** Exact modifier match — Cmd+Shift+Z must not read as Cmd+Z. */
function matchesChord(e: KeyboardEvent, chord: Chord): boolean {
  return (
    e.key.toLowerCase() === chord.key &&
    e.ctrlKey === !!chord.ctrl &&
    e.metaKey === !!chord.meta &&
    e.shiftKey === !!chord.shift &&
    !e.altKey
  );
}

/** R5: a "text input" is exactly <input type="text">, <input type="number">
 *  or <textarea>. Nothing else — an open Select's search box (type="search"),
 *  buttons, the canvas, contenteditable — so the browser keeps its own undo
 *  only where the reader is typing a value or a statement.
 *
 *  A field that declares `data-committed` (its last committed text) hands the
 *  chord back to the history once its value matches it again: after a pick or
 *  Enter the reader is no longer mid-edit, even though focus stayed put. */
const TEXT_INPUT_TYPES = new Set(['text', 'number']);

function isTextInput(target: EventTarget | null): boolean {
  const isField =
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLInputElement && TEXT_INPUT_TYPES.has(target.type));
  if (!isField) return false;
  const { committed } = target.dataset;
  return committed === undefined || target.value !== committed;
}

/**
 * Page-level undo / redo shortcuts (AP-773).
 *
 *   undo — Cmd+Z (macOS) / Ctrl+Z (Windows, Linux)
 *   redo — Cmd+Shift+Z (macOS) / Ctrl+Y, Ctrl+Shift+Z (Windows, Linux)
 *
 * Mount once per reader page. A dedicated listener rather than
 * `usePressHotkey` because the chords overlap (Cmd+Z vs Cmd+Shift+Z) and
 * need exact modifier matching.
 */
export function useHistoryHotkeys({ enabled, onResult }: Options): void {
  const onResultRef = useRef(onResult);
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  // R8: the same gate that arms the shortcuts also gates recording.
  useEffect(() => {
    setHistoryRecordingEnabled(enabled);
    return () => setHistoryRecordingEnabled(false);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;

    const handler = (e: KeyboardEvent) => {
      // R5 is the only reason to stand aside. Never defer to
      // `defaultPrevented`: Cornerstone's keyDownListener sits on the viewport
      // element and prevents every key while it has focus (which it does after
      // a drag on the canvas), so with Cmd held only the first Z would count.
      if (isTextInput(e.target)) return;

      if (UNDO_CHORDS.some((c) => matchesChord(e, c))) {
        e.preventDefault();
        // Run first, report second: the callback is optional, the undo is not.
        const result = undoHistory();
        onResultRef.current?.({ direction: 'undo', ...result });
      } else if (REDO_CHORDS.some((c) => matchesChord(e, c))) {
        e.preventDefault();
        const result = redoHistory();
        onResultRef.current?.({ direction: 'redo', ...result });
      }
    };

    // Capture phase on window: the chord is read before any element-level
    // listener (Cornerstone's, a panel's) can claim or prevent it.
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [enabled]);
}
